import {
  FAUNA_ATTACHMENTS,
  FAUNA_CLASSES,
  FAUNA_MODEL_STYLES,
  type FaunaAttachment,
  type FaunaClass,
  type FaunaDefinition,
  type FaunaModelStyle,
} from './FaunaDefinition';

/**
 * Validation for fauna catalog records, mirroring `flora/FloraValidation.ts`: field-specific,
 * human-readable errors, collected rather than thrown one at a time, with a throwing wrapper for the
 * catalog loader.
 *
 * Three checks here are not shape checks and are the reason this file is worth reading:
 *
 * - **A named animal must be named.** Everything but a `person` needs a `scientificName`, and a
 *   `person` must not carry one. "Rabbit" is four species in this region with different sizes; a
 *   catalog that cannot say which one it drew has not recorded anything.
 * - **A pose may narrow where a record can be placed, never widen it.** A `flying` pose on a record
 *   that never listed `air` means either the pose or the record is wrong, and silently accepting it
 *   would put a bird in a place the palette says it cannot go.
 * - **An asset must be licensed and must fit the record.** A licence block is required exactly when
 *   there is a mesh to license, and `asset.files` may only name poses the record declares — an entry
 *   for `hopping` on a record without a `hopping` pose is a file nothing will ever load.
 */

/** Result of validating a candidate fauna record. */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** Largest creature accepted, in meters. Generous — the point is to catch a unit slip, not to judge. */
const MAX_DIMENSION_METERS = 20;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function checkString(errors: string[], obj: Record<string, unknown>, key: string, path: string): void {
  const value = obj[key];
  if (typeof value !== 'string' || value.length === 0) {
    errors.push(`${path}.${key} must be a non-empty string`);
  }
}

/** Render an unknown value for an error message — a bad field is often not a string. */
function shown(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function checkPositive(
  errors: string[],
  obj: Record<string, unknown>,
  key: string,
  path: string,
  max = MAX_DIMENSION_METERS,
): void {
  const value = obj[key];
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    errors.push(`${path}.${key} must be a positive number`);
  } else if (value > max) {
    errors.push(`${path}.${key} of ${value} exceeds ${max} — check the units`);
  }
}

function checkOptionalPositive(
  errors: string[],
  obj: Record<string, unknown>,
  key: string,
  path: string,
): void {
  if (obj[key] === undefined) return;
  checkPositive(errors, obj, key, path);
}

function checkStringArray(
  errors: string[],
  value: unknown,
  path: string,
  { required }: { required: boolean },
): void {
  if (value === undefined && !required) return;
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
    errors.push(`${path} must be an array of strings`);
  }
}

function checkHexColor(errors: string[], value: unknown, path: string): void {
  if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
    errors.push(`${path} must be a #rrggbb colour`);
  }
}

/* --------------------------------- Section checks --------------------------------- */

function checkSize(errors: string[], value: unknown): void {
  if (!isRecord(value)) {
    errors.push('fauna.size is required');
    return;
  }
  checkPositive(errors, value, 'lengthMeters', 'fauna.size');
  checkPositive(errors, value, 'heightMeters', 'fauna.size');
  checkPositive(errors, value, 'widthMeters', 'fauna.size');
  checkOptionalPositive(errors, value, 'wingspanMeters', 'fauna.size');
}

function checkGrouping(errors: string[], value: unknown): void {
  if (!isRecord(value)) {
    errors.push('fauna.grouping is required');
    return;
  }
  const { typical, maximum } = value;
  for (const key of ['typical', 'maximum'] as const) {
    const count = value[key];
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 1) {
      errors.push(`fauna.grouping.${key} must be an integer of at least 1`);
    }
  }
  if (
    typeof typical === 'number' &&
    typeof maximum === 'number' &&
    Number.isInteger(typical) &&
    Number.isInteger(maximum) &&
    typical > maximum
  ) {
    errors.push('fauna.grouping.typical must not exceed fauna.grouping.maximum');
  }
  checkPositive(errors, value, 'spreadMeters', 'fauna.grouping', 100);
}

/** Returns the declared attachments, so later checks can test poses against them. */
function checkAttachment(errors: string[], candidate: Record<string, unknown>): FaunaAttachment[] {
  const value = candidate.attachment;
  if (!Array.isArray(value) || value.length === 0) {
    errors.push('fauna.attachment must be a non-empty array');
    return [];
  }

  const unknown = value.filter((entry) => !FAUNA_ATTACHMENTS.includes(entry as FaunaAttachment));
  if (unknown.length > 0) {
    errors.push(
      `fauna.attachment contains unknown values (${unknown.join(', ')}); must be one of: ${FAUNA_ATTACHMENTS.join(', ')}`,
    );
  }

  const allowed = value.filter((entry): entry is FaunaAttachment =>
    FAUNA_ATTACHMENTS.includes(entry as FaunaAttachment),
  );
  if (new Set(allowed).size !== allowed.length) {
    errors.push('fauna.attachment must not repeat a value');
  }

  // A flying record has to say how high it flies, or placement has no answer but zero — which puts a
  // hovering hummingbird in the grass.
  if (allowed.includes('air')) {
    if (candidate.hoverHeightMeters === undefined) {
      errors.push('fauna.hoverHeightMeters is required when attachment includes "air"');
    } else {
      checkPositive(errors, candidate, 'hoverHeightMeters', 'fauna');
    }
  } else if (candidate.hoverHeightMeters !== undefined) {
    errors.push('fauna.hoverHeightMeters is only meaningful when attachment includes "air"');
  }

  return allowed;
}

/** Returns the declared pose ids, for the asset check to test its file map against. */
function checkPoses(
  errors: string[],
  candidate: Record<string, unknown>,
  allowedAttachments: readonly FaunaAttachment[],
): string[] {
  const poses = candidate.poses;
  if (!Array.isArray(poses) || poses.length === 0) {
    errors.push('fauna.poses must be a non-empty array');
    return [];
  }

  const ids: string[] = [];
  poses.forEach((pose, index) => {
    const path = `fauna.poses[${index}]`;
    if (!isRecord(pose)) {
      errors.push(`${path} must be an object`);
      return;
    }
    checkString(errors, pose, 'id', path);
    checkString(errors, pose, 'name', path);
    checkOptionalPositive(errors, pose, 'heightMeters', path);
    checkOptionalPositive(errors, pose, 'lengthMeters', path);

    if (typeof pose.id === 'string' && pose.id.length > 0) {
      if (ids.includes(pose.id)) errors.push(`${path}.id "${pose.id}" is duplicated`);
      ids.push(pose.id);
    }

    if (pose.attachment !== undefined) {
      if (!FAUNA_ATTACHMENTS.includes(pose.attachment as FaunaAttachment)) {
        errors.push(`${path}.attachment "${shown(pose.attachment)}" is not a known attachment`);
      } else if (
        allowedAttachments.length > 0 &&
        !allowedAttachments.includes(pose.attachment as FaunaAttachment)
      ) {
        // The narrowing rule. A pose may pick one of the record's attachments; it may not invent one.
        errors.push(
          `${path}.attachment "${shown(pose.attachment)}" is not in fauna.attachment — a pose may narrow where a record can be placed, not widen it`,
        );
      }
    }
  });

  const defaultPoseId = candidate.defaultPoseId;
  if (defaultPoseId !== undefined) {
    if (typeof defaultPoseId !== 'string' || !ids.includes(defaultPoseId)) {
      errors.push(`fauna.defaultPoseId "${shown(defaultPoseId)}" does not name a declared pose`);
    }
  }

  return ids;
}

function checkRendering(errors: string[], value: unknown): void {
  if (!isRecord(value)) {
    errors.push('fauna.rendering is required');
    return;
  }
  if (!FAUNA_MODEL_STYLES.includes(value.style as FaunaModelStyle)) {
    errors.push(`fauna.rendering.style must be one of: ${FAUNA_MODEL_STYLES.join(', ')}`);
  }

  const colors = value.colors;
  if (!isRecord(colors)) {
    errors.push('fauna.rendering.colors is required');
  } else {
    checkHexColor(errors, colors.body, 'fauna.rendering.colors.body');
    checkHexColor(errors, colors.accent, 'fauna.rendering.colors.accent');
  }

  if (typeof value.yawOffsetDegrees !== 'number' || !Number.isFinite(value.yawOffsetDegrees)) {
    errors.push('fauna.rendering.yawOffsetDegrees must be a finite number');
  }
  if (
    typeof value.sinkMeters !== 'number' ||
    !Number.isFinite(value.sinkMeters) ||
    value.sinkMeters < 0
  ) {
    errors.push('fauna.rendering.sinkMeters must be a number of at least 0');
  }
}

function checkAsset(errors: string[], value: unknown, poseIds: readonly string[]): void {
  if (value === undefined) return; // A record with no meshes is complete. See FaunaDefinition.
  if (!isRecord(value)) {
    errors.push('fauna.asset must be an object when present');
    return;
  }

  const files = value.files;
  if (!isRecord(files)) {
    errors.push('fauna.asset.files must be an object of pose id → file name');
  } else {
    for (const [poseId, file] of Object.entries(files)) {
      if (typeof file !== 'string' || file.length === 0) {
        errors.push(`fauna.asset.files.${poseId} must be a non-empty file name`);
      }
      if (poseIds.length > 0 && !poseIds.includes(poseId)) {
        errors.push(`fauna.asset.files.${poseId} names a pose the record does not declare`);
      }
    }
  }

  if (value.triangles !== undefined) {
    if (
      typeof value.triangles !== 'number' ||
      !Number.isInteger(value.triangles) ||
      value.triangles <= 0
    ) {
      errors.push('fauna.asset.triangles must be a positive integer when present');
    }
  }

  const license = value.license;
  if (!isRecord(license)) {
    errors.push(
      'fauna.asset.license is required — a record that names a third-party mesh must say where it came from',
    );
    return;
  }
  for (const field of ['spdx', 'author', 'sourceUrl'] as const) {
    checkString(errors, license, field, 'fauna.asset.license');
  }
}

/* ----------------------------------- Entry points ---------------------------------- */

/**
 * Validate a candidate fauna record. Returns field-specific errors and never throws — see
 * {@link assertValidFaunaDefinition} for the loader's version.
 */
export function validateFaunaDefinition(candidate: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isRecord(candidate)) {
    return { valid: false, errors: ['fauna record must be an object'] };
  }

  checkString(errors, candidate, 'id', 'fauna');
  checkString(errors, candidate, 'name', 'fauna');

  if (typeof candidate.schemaVersion !== 'number' || !Number.isInteger(candidate.schemaVersion)) {
    errors.push('fauna.schemaVersion is required and must be an integer');
  }

  const faunaClass = candidate.class;
  if (!FAUNA_CLASSES.includes(faunaClass as FaunaClass)) {
    errors.push(`fauna.class must be one of: ${FAUNA_CLASSES.join(', ')}`);
  }

  checkStringArray(errors, candidate.tags, 'fauna.tags', { required: true });
  checkStringArray(errors, candidate.commonNames, 'fauna.commonNames', { required: false });

  // An animal we cannot name is a guess; a scale figure has no species to name.
  if (faunaClass === 'person') {
    if (candidate.scientificName !== undefined) {
      errors.push('fauna.scientificName must be absent for a person record');
    }
    if (candidate.eyeHeightMeters === undefined) {
      errors.push('fauna.eyeHeightMeters is required for a person record — it is the camera height');
    } else {
      checkPositive(errors, candidate, 'eyeHeightMeters', 'fauna');
    }
  } else if (FAUNA_CLASSES.includes(faunaClass as FaunaClass)) {
    checkString(errors, candidate, 'scientificName', 'fauna');
    if (candidate.eyeHeightMeters !== undefined) {
      errors.push('fauna.eyeHeightMeters applies to person records only');
    }
  }

  checkSize(errors, candidate.size);
  checkGrouping(errors, candidate.grouping);

  if (candidate.sizeVariation !== undefined) {
    const variation = candidate.sizeVariation;
    if (typeof variation !== 'number' || !Number.isFinite(variation) || variation < 0 || variation > 0.5) {
      errors.push('fauna.sizeVariation must be a number in [0, 0.5]');
    }
  }

  const attachments = checkAttachment(errors, candidate);
  const poseIds = checkPoses(errors, candidate, attachments);
  checkRendering(errors, candidate.rendering);
  checkAsset(errors, candidate.asset, poseIds);

  // A figure's eyes are in its head. Checked after both are known to be sane, so a bad height does
  // not produce two complaints about the same typo.
  const size = candidate.size;
  if (
    faunaClass === 'person' &&
    isRecord(size) &&
    typeof size.heightMeters === 'number' &&
    typeof candidate.eyeHeightMeters === 'number' &&
    candidate.eyeHeightMeters >= size.heightMeters
  ) {
    errors.push('fauna.eyeHeightMeters must be less than fauna.size.heightMeters');
  }

  return { valid: errors.length === 0, errors };
}

/** Validate, throwing on the first failure with every error in the message. */
export function assertValidFaunaDefinition(raw: unknown): asserts raw is FaunaDefinition {
  const result = validateFaunaDefinition(raw);
  if (!result.valid) {
    const id = isRecord(raw) && typeof raw.id === 'string' ? raw.id : '<no id>';
    throw new Error(`Invalid fauna record ${id}:\n  ${result.errors.join('\n  ')}`);
  }
}
